import React, { useEffect } from 'react';
import QuestionPanel from "../../structure/QuestionPanel";
import { useFormikContext } from "formik";
import RadioGroup from "../../structure/RadioGroup";
import Tooltip from "../../input/Tooltip";
import { useAuth } from "oidc-react";
import { EmployerFormValues } from "../../common/EmployerFormValues";
import { FederationProduct } from "../../enums/FederationProduct";
import { BenefitType, findTitleFor } from "../../enums/BenefitType";
import Checkbox from "../../input/Checkbox";
import { MemberGroupCoverages } from "../../../generated/graphql";
import _ from "lodash";
import { useParams } from "react-router-dom";
import { findInitialValuesFor } from "../../../service/FormService";
import { useCoverages } from "../../../hooks/useCoverages";
import { GenericRelationship } from "../../enums/GenericRelationship";
import { Relationship } from "../../enums/Relationship";
import { useQuery } from "@apollo/client";
import { VALIDATE_PAID_FAMILY_AND_MEDICAL_LEAVE_SUPPORTED_QUERY } from "../../../Queries";
import { PfmlEmployerFormValues } from "../../pfml/PfmlEmployerFormValues";

const ProductQuestionnaire = () => {
    const { initialValues, values, setFieldValue, resetForm } = useFormikContext<EmployerFormValues>();
    const { claimId, coverage } = useParams()

    const auth = useAuth();
    const user = auth?.userData;
    const signedIn = !!user;
    const policyNumber = values.policyNumber
    const privacyId = values.privacyId
    const genericRelationship = values.genericRelationship
    const disability = values.disability;
    const pfml = values.pfml;
    const life = values.life;
    const hospitalIndemnity = values.hospitalIndemnity;
    const accident = values.accident;
    const criticalIllness = values.criticalIllness;
    const party = values.party;
    const effectiveDate = '9999-12-31'
    const isEmployee = GenericRelationship.EMPLOYEE === genericRelationship;
    const workState = (values as PfmlEmployerFormValues).workState

    const { data: coveragesData } = useCoverages(policyNumber, privacyId, genericRelationship, effectiveDate, auth);
    const coverages = coveragesData?.findCoverages
    const memberGroupCoverages = coverages as MemberGroupCoverages;

    const { data: validatePaidFamilyAndMedicalLeaveSupported } = useQuery(VALIDATE_PAID_FAMILY_AND_MEDICAL_LEAVE_SUPPORTED_QUERY, {
        variables: {
            workState
        },
        skip: !workState
    })
    const stateSupportsPfml = validatePaidFamilyAndMedicalLeaveSupported?.validatePaidFamilyAndMedicalLeaveSupported

    const showAllProducts = !signedIn || !coverages
    const hasLife = showAllProducts || !!(coverages?.[FederationProduct.VOLUNTARY_TERM_LIFE] || coverages?.[FederationProduct.GROUP_TERM_LIFE])
    const hasPaidLeave = stateSupportsPfml && (showAllProducts || !!(memberGroupCoverages?.[FederationProduct.PAID_MEDICAL_LEAVE] || memberGroupCoverages?.[FederationProduct.PAID_FAMILY_LEAVE]))
    const hasDisability = isEmployee && (showAllProducts || !!(coverages?.[FederationProduct.SHORT_TERM_DISABILITY] || coverages?.[FederationProduct.LONG_TERM_DISABILITY]))
    const showLife = values.deceased === "Yes" || BenefitType.LIFE === coverage;
    const takingLeave = values.takingLeave === "Yes";
    const showPaidLeave = (hasPaidLeave && takingLeave) || BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE === coverage;
    const showDisability = values.earningsLoss === "Yes" || BenefitType.DISABILITY === coverage;
    const showHospitalIndemnity = claimId && BenefitType.HOSPITAL_INDEMNITY === coverage;
    const showAccident = claimId && BenefitType.ACCIDENT === coverage;
    const showCriticalIllness = claimId && BenefitType.CRITICAL_ILLNESS === coverage;

    useEffect(() => {
        if(!(disability || pfml || life || hospitalIndemnity || accident || criticalIllness)) {
            return;
        }
        const defaultInitialValues = {
            ...(disability ? findInitialValuesFor(party, BenefitType.DISABILITY) : {}),
            ...(pfml ? findInitialValuesFor(party, BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE) : {}),
            ...(life ? findInitialValuesFor(party, BenefitType.LIFE) : {}),
            ...(hospitalIndemnity ? findInitialValuesFor(party, BenefitType.HOSPITAL_INDEMNITY) : {}),
            ...(accident ? findInitialValuesFor(party, BenefitType.ACCIDENT) : {}),
            ...(criticalIllness ? findInitialValuesFor(party, BenefitType.CRITICAL_ILLNESS) : {})
        }
        if(_.isEmpty(defaultInitialValues)) {
            return;
        }
        const combinedInitialValuesKeys = _.keys(defaultInitialValues);
        if(_.isEqual(_.keys(initialValues), combinedInitialValuesKeys)) {
            return;
        }
        const newInitialValues = _.merge(defaultInitialValues, _.pick(values, combinedInitialValuesKeys)) as EmployerFormValues
        resetForm({ values: newInitialValues })
    }, [disability, pfml, life, hospitalIndemnity, accident, criticalIllness, party, initialValues, values, resetForm]);

    return (
        <QuestionPanel headerText="Let's see what benefits are available to be claimed for your employee."
                       detailsText={showPaidLeave ? "If your employee is applying for bonding, please choose family member as the patient's relationship." : ""}
                       panelContent={<>
                           <RadioGroup id="generic-relationship"
                                       label="What is the patient's relationship to your employee?"
                                       required={true}
                                       radioProps={[
                                           { id: "employee", text: GenericRelationship.EMPLOYEE, changeCallback: () => { setFieldValue('relationship', Relationship.EMPLOYEE) } },
                                           { id: "family-member-or-covered-dependent", text: GenericRelationship.FAMILY_MEMBER_OR_COVERED_DEPENDENT, changeCallback: () => {
                                               setFieldValue('relationship', '');
                                               setFieldValue('earningsLoss', '');
                                               setFieldValue('disability', false);
                                           } }
                                       ]}
                                       labelContent={<Tooltip text="The patient is the person who is receiving care (from an employee or physician) or the deceased."/>}/>
                           {genericRelationship && hasLife && <RadioGroup id="deceased"
                                                   label={`Is your ${values.genericRelationship?.toLowerCase()} deceased?`}
                                                   required={true}
                                                   radioProps={[
                                                       { id: "yes", text: "Yes", changeCallback: () => {
                                                               if (!showAllProducts && hasLife) {
                                                                   setFieldValue("life", true)
                                                           }
                                                       }},
                                                       { id: "no", text: "No", changeCallback: () => setFieldValue("life", false) }
                                                   ]}/>}
                           {(hasPaidLeave || hasDisability) && <RadioGroup id="taking-leave"
                                                        label="Has your employee missed work / is anticipated to miss work?"
                                                        required={true}
                                                        radioProps={[
                                                            { id: "yes", text: "Yes", changeCallback: () => {
                                                                    if (!showAllProducts && hasPaidLeave) {
                                                                        setFieldValue("pfml", true)
                                                                }
                                                            }},
                                                            { id: "no", text: "No", changeCallback: () => ['pfml', 'disability'].forEach((checkboxName) => setFieldValue(checkboxName, false)) }
                                                        ]}/>}
                           {(hasDisability && takingLeave) && <RadioGroup id="earnings-loss"
                                                                        label="Has your employee experienced an earnings loss?"
                                                                        required={true}
                                                                        radioProps={[
                                                                            { id: "yes", text: "Yes", changeCallback: () => {
                                                                                if (!showAllProducts && hasDisability) {
                                                                                    setFieldValue("disability", true)
                                                                                }
                                                                            }},
                                                                            { id: "no", text: "No", changeCallback: () => setFieldValue("disability", false) }
                                                                        ]}/>}
                           <div className="flex-gap">
                               <div>
                                   Please check the benefits for which you wish to file.
                                   <strong> Only select coverages that this employee is enrolled in and you and your employee are claiming benefits for. </strong>
                                   <span className="pds-u-typography-color-error">*</span>
                               </div>
                               <div>
                                   {showLife &&
                                       <Checkbox id="life" label={findTitleFor(BenefitType.LIFE)}
                                                 labelContent={buildTooltipFor(BenefitType.LIFE)}/>}
                                   {showPaidLeave &&
                                       <Checkbox id="pfml" label={findTitleFor(BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE)}
                                              labelContent={buildTooltipFor(BenefitType.PAID_FAMILY_AND_MEDICAL_LEAVE)}/>}
                                   {(isEmployee && showDisability) &&
                                       <Checkbox id="disability" label={findTitleFor(BenefitType.DISABILITY)}
                                                 labelContent={buildTooltipFor(BenefitType.DISABILITY)}/>}
                                   {showHospitalIndemnity &&
                                       <Checkbox id="hospital-indemnity" label={findTitleFor(BenefitType.HOSPITAL_INDEMNITY)}/>}
                                   {showAccident &&
                                       <Checkbox id="accident" label={findTitleFor(BenefitType.ACCIDENT)}/>}
                                   {showCriticalIllness &&
                                       <Checkbox id="critical-illness" label={findTitleFor(BenefitType.CRITICAL_ILLNESS)}/>}
                               </div>
                           </div>
                       </>}/>
    )

    function buildTooltipFor(benefit: BenefitType) {
        return <Tooltip text={`Only check if Principal is your ${findTitleFor(benefit).toLowerCase()} carrier`}/>
    }
};
export default ProductQuestionnaire;